/*
 * Beyond the Fridge — London street sign generator (screen 15).
 *
 * Ported from the design's Component logic. The panel geometry is taken from the two supplied
 * master artworks (assets/btf-img-sign-master-75x40.svg and -100x40.svg) rather than estimated:
 * both are 1007.25 units tall and their widths differ by 708.67 units for a 250 mm difference in
 * panel width, so one unit is 0.3528 mm and each viewBox width IS the panel width. Both panels
 * are 400 mm deep — one depth, two lengths. The artwork box is centred in the panel and the
 * panel is centred in its square photograph, so the type overlay and the photographed panel
 * share one centre and one scale.
 *
 * The postcode slot is reserved UNCONDITIONALLY at the master's own width, so an empty field and
 * a four-character code (SW18, WC2A) set the sign identically. Long names reduce the type, as
 * they do on the real signs; the leading shrinks with it about the block centre so the block
 * keeps its proportions.
 *
 * Elements:
 *   <btf-signs-panel>  one rendered panel — the photographed blank with the live type over it
 *   <btf-signs-maker>  the page controller: wires the four fields, their counters and caps, the
 *                      width toggle, the hidden variant id on the product form, the basket
 *                      label, the panel/price line and the capital-height readout
 */

// The print file helpers (assets/btf-print-jpeg.js)
const printJpegModule = () => import((window.btfAssets && window.btfAssets.printJpeg) || './btf-print-jpeg.js');

// mm per master unit, the master's baselines, type sizes and rule weight, all in units
const MASTER = {
  unit: 0.3528,
  vbh: 1007.25,
  y1: 308.08, // first name-line baseline
  y2: 665.87, // second name-line baseline
  rule: 785.14, // the black rule
  sy: 975.23, // red line baseline
  nameFs: 360, // name and postcode type size
  strapFs: 144, // red line type size
  strapStretch: 1.05, // the master stretches the red line horizontally
  ruleW: 22,
  space: 0.647, // the master's own word space before the postcode, in ems
  cap: 0.766, // capital height as a fraction of type size
  // the master's <defs> arrived with its class styles stripped, so the weight is taken from the
  // product photography instead: the name is the Bold, not the Black
  weight: 700,
};

// The master's own postcode slot: its codeX (1273.10) to its right margin (2019.84)
const CODE_SLOT = 746.74;

// Character limits derived from the master geometry at full type size. Line 2 is shorter
// because the postcode shares its line; the postcode takes up to four characters.
export const SIGN = {
  small: {
    w: 750,
    h: 400,
    label: 'Small',
    mm: '750 × 400 mm',
    vbw: 2125.98,
    mx: 106.14,
    sx: 117.69,
    pw: 65.29, // the panel's opaque width as a percentage of its square photograph
    max: { l1: 11, l2: 6, code: 4, strap: 23 },
  },
  large: {
    w: 1000,
    h: 400,
    label: 'Large',
    mm: '1000 × 400 mm',
    vbw: 2834.65,
    mx: 205.5,
    sx: 217.04,
    pw: 87.58,
    max: { l1: 14, l2: 9, code: 4, strap: 30 },
  },
};

const FACE = "'Sign Face', 'Archivo', sans-serif";
const SVG_NS = 'http://www.w3.org/2000/svg';
const ROLES = ['l1', 'l2', 'code', 'strap'];

// The lettering is reduced below this fraction of the master's own type size before the page
// says so. The design ships the note's copy but leaves its flag unset, so the threshold is
// ours: "getting small" starts once the caps are under about nine tenths of the master's.
const WARN_SCALE = 0.88;

// real advance width of a run, in master units, measured in the licensed face
let ctx = null;
function measure(str, fs, weight, stretch) {
  if (!str) return 0;
  if (!ctx) ctx = document.createElement('canvas').getContext('2d');
  ctx.font = weight + ' ' + fs + 'px ' + FACE;
  return ctx.measureText(str).width * (stretch || 1);
}

/**
 * Solves the panel: which lines are set, at what size, on which baselines, and where the
 * postcode ends. `size` is 'small' or 'large'; the four words are already uppercase.
 */
export function geometry(size, l1, l2, code, strap) {
  const P = SIGN[size] || SIGN.small;
  const M = MASTER;

  const lines = [String(l1 || '').trim(), String(l2 || '').trim()].filter((l) => l.length);
  if (!lines.length) lines.push(' ');
  const postcode = String(code || '').trim();
  const red = String(strap || '').trim();

  // the type runs from the master's left inset to a matching inset on the right
  const avail = P.vbw - P.mx * 2;
  const gap = M.space * M.nameFs;
  // The postcode slot is reserved at a constant width — the master's own — whether or not
  // anything is typed in it, so empty, W1, SW1, SW18 and WC2A all set identically.
  const slot = gap + CODE_SLOT;
  const widest = Math.max.apply(
    null,
    lines.map((l, i) => measure(l, M.nameFs, M.weight) + (i === lines.length - 1 ? slot : 0))
  );
  const scale = widest > avail ? avail / widest : 1;
  const fs = M.nameFs * scale;

  // Two lines sit on the master's own baselines at full size; a single line centres between
  // them. When the type shrinks the leading shrinks with it about the block centre, so the
  // block's proportions do not change as soon as a character is added.
  const mid = (M.y1 + M.y2) / 2;
  const half = ((M.y2 - M.y1) / 2) * scale;
  const ys = lines.length > 1 ? [mid - half, mid + half] : [mid];

  // the postcode is ranged right to the panel's own margin, so two, three or four characters
  // always end in the same place
  const codeX = P.vbw - P.mx;

  const strapRun = measure(red, M.strapFs, M.weight, M.strapStretch);
  const strapFs = strapRun > avail ? (M.strapFs * avail) / strapRun : M.strapFs;

  return {
    P,
    M,
    lines,
    ys,
    code: postcode,
    strap: red,
    fs,
    strapFs,
    codeX,
    capMm: fs * M.cap * M.unit,
    twoLine: lines.length > 1,
    scale,
    tight: scale < 0.995,
    tooSmall: scale < WARN_SCALE,
  };
}

function svgText(str, fs, fill, transform) {
  const t = document.createElementNS(SVG_NS, 'text');
  t.setAttribute('transform', transform);
  t.setAttribute('font-family', FACE);
  t.setAttribute('font-weight', String(MASTER.weight));
  t.setAttribute('font-size', fs.toFixed(2));
  t.setAttribute('fill', fill);
  t.style.whiteSpace = 'pre';
  t.textContent = str;
  return t;
}

/**
 * One rendered panel: the photographed blank with the customer's words set over it on the
 * master's own baselines and margins. The photographs are authored in Liquid (a real asset
 * URL each) and this only hides the one that is not in use, so nothing is ever requested twice.
 */
class SignsPanel extends HTMLElement {
  #raf = 0;
  #art = null;
  #want = '';

  static get observedAttributes() {
    return ['data-size', 'data-l1', 'data-l2', 'data-code', 'data-strap'];
  }

  connectedCallback() {
    this.#schedule();
    // The generator measures real glyph widths, so it has to draw again once the licensed face
    // has loaded — before that the fallback's metrics would set the type.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        ctx = null;
        this.refit();
      });
    }
  }

  attributeChangedCallback() {
    if (this.isConnected) this.#schedule();
  }

  /** Throw away the cached drawing and set the words again. */
  refit() {
    this.#want = '';
    this.#schedule();
  }

  #schedule() {
    if (this.#raf) return;
    this.#raf = requestAnimationFrame(() => {
      this.#raf = 0;
      this.render();
    });
  }

  render() {
    const size = this.dataset.size === 'large' ? 'large' : 'small';
    const g = geometry(size, this.dataset.l1, this.dataset.l2, this.dataset.code, this.dataset.strap);
    const P = g.P;

    // the frame carries the panel plus the photograph's own headroom, as the design draws it
    this.style.aspectRatio = P.w * 1.12 + ' / ' + (P.h + P.w * 0.12);

    this.querySelectorAll('[data-btf-panel]').forEach((img) => {
      const on = img.dataset.btfPanel === size;
      const p = SIGN[img.dataset.btfPanel] || P;
      img.style.width = ((89.29 / p.pw) * 100).toFixed(3) + '%';
      img.hidden = !on;
    });

    // the solved type sizes are part of the key, so a re-fit after the face loads redraws
    const want = [size, g.lines.join('\u0001'), g.code, g.strap, g.fs.toFixed(2), g.strapFs.toFixed(2)].join('\u0002');
    if (want === this.#want && this.#art) return;
    this.#want = want;

    const svg = document.createElementNS(SVG_NS, 'svg');
    svg.setAttribute('viewBox', '0 0 ' + P.vbw + ' ' + MASTER.vbh);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('class', 'btf-signs-panel__art');

    svg.appendChild(svgText(g.lines[0], g.fs, '#1d1d1b', 'translate(' + P.mx.toFixed(2) + ' ' + g.ys[0].toFixed(2) + ')'));
    if (g.twoLine) {
      svg.appendChild(
        svgText(g.lines[1], g.fs, '#1d1d1b', 'translate(' + P.mx.toFixed(2) + ' ' + g.ys[1].toFixed(2) + ')')
      );
    }
    if (g.code) {
      const c = svgText(
        g.code,
        g.fs,
        '#c8102e',
        'translate(' + g.codeX.toFixed(2) + ' ' + g.ys[g.ys.length - 1].toFixed(2) + ')'
      );
      c.setAttribute('text-anchor', 'end');
      svg.appendChild(c);
    }
    const rule = document.createElementNS(SVG_NS, 'line');
    rule.setAttribute('x1', '0');
    rule.setAttribute('y1', String(MASTER.rule));
    rule.setAttribute('x2', String(P.vbw));
    rule.setAttribute('y2', String(MASTER.rule));
    rule.setAttribute('stroke', '#1d1d1b');
    rule.setAttribute('stroke-width', String(MASTER.ruleW));
    svg.appendChild(rule);
    if (g.strap) {
      svg.appendChild(
        svgText(
          g.strap,
          g.strapFs,
          '#c8102e',
          'translate(' + P.sx.toFixed(2) + ' ' + MASTER.sy.toFixed(2) + ') scale(' + MASTER.strapStretch + ' 1)'
        )
      );
    }

    if (this.#art) this.#art.remove();
    this.#art = svg;
    this.appendChild(svg);
    this.dataset.ready = 'true';
  }
}

/**
 * The page controller. The personaliser sells two products rather than two variants of one, so
 * the width toggle swaps the hidden `id` on the product form as well as the panel — Horizon's
 * own product-form-component posts whatever that field carries, so the AJAX cart, the error
 * states and the cart drawer all keep working.
 */
class SignsMaker extends HTMLElement {
  #ready = false;
  #size = 'small';

  connectedCallback() {
    if (this.#ready) return;
    this.#ready = true;
    this.#size = this.dataset.size === 'large' ? 'large' : 'small';

    this.addEventListener('input', (e) => {
      if (e.target instanceof Element && e.target.matches('[data-btf-signs-field]')) this.#render();
    });
    // The customer's go-ahead (snippets/btf-approve.liquid). The box is a required field, so the
    // browser refuses the submit until it is ticked; the browser's own bubble gives way to our
    // nudge. The time it was ticked rides in a hidden field, and once the line is in, the basket
    // is tidied so the print file reads first.
    this.addEventListener(
      'invalid',
      (e) => {
        if (!(e.target instanceof Element) || !e.target.matches('[data-btf-approve-box]')) return;
        e.preventDefault();
        printJpegModule().then((PJ) => PJ.approval(this));
      },
      true
    );
    this.addEventListener(
      'submit',
      () => {
        const box = this.querySelector('[data-btf-approve-box]');
        if (box && box.checked) [2000, 5000, 10000].forEach((ms) => setTimeout(() => printJpegModule().then((PJ) => PJ.tidyBasket()), ms));
      },
      true
    );
    this.addEventListener('change', (e) => {
      if (!(e.target instanceof Element) || !e.target.matches('[data-btf-approve-box]')) return;
      const field = this.querySelector('[data-btf-signs-approved]');
      if (field) printJpegModule().then((PJ) => (field.value = e.target.checked ? PJ.approvedNow() : ''));
    });
    this.addEventListener('click', (e) => {
      const t = e.target.closest && e.target.closest('[data-btf-size]');
      if (!t || !this.contains(t)) return;
      this.#setSize(t.dataset.btfSize);
    });
    this.querySelectorAll('[data-btf-signs-field]').forEach((i) => {
      i.addEventListener('focus', () => {
        if (i.dataset.btfSelect !== 'false') i.select();
      });
    });

    // A shared link (assets/btf-share.js) carries the width and the words
    const q = new URLSearchParams(window.location.search);
    const shared = !q.has('edit') && ['size', ...ROLES].some((k) => q.has(k)) ? q : null;
    if (shared) {
      if (shared.has('size')) this.#size = shared.get('size') === 'large' ? 'large' : 'small';
      ROLES.forEach((role) => {
        const input = this.#field(role);
        if (input && shared.has(role)) input.value = shared.get(role);
      });
    }

    this.#setSize(this.#size, true);
    this.#share(Boolean(shared));

    // the generator measures real glyph widths, so the readouts and the panel re-fit once the
    // licensed face has loaded
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        ctx = null;
        this.#render();
        this.querySelectorAll('btf-signs-panel').forEach((p) => {
          if (typeof p.refit === 'function') p.refit();
        });
      });
    }
  }

  // "Send it to someone before you decide": shown once the customer changes the words or width
  /*
   * The sign as a picture, for the share strip (assets/btf-share.js): the panel photograph with
   * the lettering drawn over it where the page shows it. A picture cannot use the page's fonts,
   * so Sign Face goes inside the drawing as data.
   */
  async #picture() {
    // Phones show no live sign (the preview column is desktop-only), so the sign is drawn off
    // screen at a fixed width for the picture, then removed
    let panel = Array.from(this.querySelectorAll('btf-signs-panel')).find((el) => el.getBoundingClientRect().width > 0);
    let offscreen = null;
    if (!panel) {
      const source = this.querySelector('btf-signs-panel');
      if (!source) return null;
      offscreen = document.createElement('div');
      offscreen.setAttribute('aria-hidden', 'true');
      offscreen.style.cssText = 'position:fixed;left:-10000px;top:0;width:900px;pointer-events:none;';
      panel = source.cloneNode(true);
      panel.querySelectorAll('svg').forEach((n) => n.remove());
      offscreen.appendChild(panel);
      document.body.appendChild(offscreen);
      await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
      await Promise.all(
        Array.from(panel.querySelectorAll('img')).map((img) => (img.complete ? null : new Promise((r) => (img.onload = img.onerror = r))))
      );
    }
    try {
      return await this.#drawSign(panel);
    } finally {
      if (offscreen) offscreen.remove();
    }
  }

  async #drawSign(panel) {
    const art = panel && panel.querySelector('svg');
    const photo = panel && Array.from(panel.querySelectorAll('[data-btf-panel]')).find((img) => !img.hidden);
    if (!panel || !art || !photo) return null;
    const box = panel.getBoundingClientRect();
    const k = 1600 / box.width;
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(box.width * k);
    canvas.height = Math.round(box.height * k);
    const ctx = canvas.getContext('2d');
    const load = (src) =>
      new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('the sign could not be drawn'));
        img.src = src;
      });
    const at = (el) => {
      const r = el.getBoundingClientRect();
      return [(r.left - box.left) * k, (r.top - box.top) * k, r.width * k, r.height * k];
    };

    const plate = await load(photo.currentSrc || photo.src);
    ctx.drawImage(plate, ...at(photo));

    const copy = art.cloneNode(true);
    const [, , w, h] = at(art);
    copy.setAttribute('width', String(Math.round(w)));
    copy.setAttribute('height', String(Math.round(h)));
    copy.removeAttribute('class');
    await this.#embedFace(copy);
    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml' }));
    try {
      const letters = await load(url);
      ctx.drawImage(letters, ...at(art));
    } finally {
      URL.revokeObjectURL(url);
    }
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/png'));
  }

  // A picture cannot use the page's fonts, so Sign Face goes inside the drawing as data
  async #embedFace(svg) {
    if (!this.dataset.signFont) return;
    if (!this._fontData) {
      const buf = await fetch(this.dataset.signFont).then((r) => r.arrayBuffer());
      let bin = '';
      const bytes = new Uint8Array(buf);
      for (let i = 0; i < bytes.length; i += 0x8000) bin += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
      this._fontData = btoa(bin);
    }
    const style = document.createElementNS(SVG_NS, 'style');
    style.textContent = "@font-face{font-family:'Sign Face';font-weight:700;src:url(data:font/ttf;base64," + this._fontData + ") format('truetype');}";
    svg.insertBefore(style, svg.firstChild);
  }

  /*
   * The print picture for the order page: the white panel at its full 400 mm depth with the
   * lettering where the master sets it (the master's artwork box is centred in the panel), plus
   * 5 mm of bleed on every edge, the rule running out into it. 150 dpi, as a JPEG.
   */
  async #printPicture() {
    const art = Array.from(this.querySelectorAll('btf-signs-panel > svg')).find(Boolean);
    if (!art) return null;
    const P = SIGN[this.#size];
    const M = MASTER;
    const bleed = 5;
    const padX = bleed / M.unit;
    const padY = ((P.h - M.vbh * M.unit) / 2 + bleed) / M.unit;
    const dpmm = 150 / 25.4;
    const width = Math.round((P.w + bleed * 2) * dpmm);
    const height = Math.round((P.h + bleed * 2) * dpmm);
    const copy = art.cloneNode(true);
    copy.removeAttribute('class');
    copy.setAttribute('viewBox', [-padX, -padY, P.vbw + padX * 2, M.vbh + padY * 2].map((v) => v.toFixed(2)).join(' '));
    copy.setAttribute('width', String(width));
    copy.setAttribute('height', String(height));
    copy.querySelectorAll('line').forEach((l) => {
      l.setAttribute('x1', (-padX).toFixed(2));
      l.setAttribute('x2', (P.vbw + padX).toFixed(2));
    });
    await this.#embedFace(copy);
    const url = URL.createObjectURL(new Blob([new XMLSerializer().serializeToString(copy)], { type: 'image/svg+xml' }));
    try {
      const img = await new Promise((resolve, reject) => {
        const i = new Image();
        i.onload = () => resolve(i);
        i.onerror = () => reject(new Error('the sign could not be drawn'));
        i.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      const blob = await new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.92));
      // The proof: the same sign, 1200 px wide, for the basket and the order
      const pw = 1200;
      const proof = document.createElement('canvas');
      proof.width = pw;
      proof.height = Math.round((height * pw) / width);
      const pc = proof.getContext('2d');
      pc.imageSmoothingQuality = 'high';
      pc.drawImage(canvas, 0, 0, proof.width, proof.height);
      const proofBlob = await new Promise((resolve) => proof.toBlob(resolve, 'image/jpeg', 0.88));
      canvas.width = canvas.height = proof.width = proof.height = 0;
      if (!blob) return null;
      return { blob: await (await printJpegModule()).stampDpi(blob), proof: proofBlob };
    } finally {
      URL.revokeObjectURL(url);
    }
  }

  /*
   * The print picture rides in a hidden file field on the form, so Horizon's product form posts
   * it with the line (a multipart post, as Create Your Own's files do). Horizon adds the line
   * before a submit event could wait for a drawing, so the picture is drawn ahead: the field is
   * emptied the moment the words or the width change, and filled again once typing stops. A
   * stale picture can never go with the order; at worst none does, and the words are on the
   * line either way.
   */
  #queuePrint() {
    const field = this.querySelector('[data-btf-signs-print]');
    if (!field) return;
    field.value = '';
    const proofField = this.querySelector('[data-btf-signs-proof]');
    if (proofField) proofField.value = '';
    clearTimeout(this._printTimer);
    const ticket = (this._printTicket = (this._printTicket || 0) + 1);
    this._printTimer = setTimeout(async () => {
      try {
        const pic = await this.#printPicture();
        if (!pic || ticket !== this._printTicket) return;
        const blob = pic.blob;
        const words = ROLES.map((r) => (this.#field(r) || {}).value || '').filter(Boolean).join('-');
        const name = ('london-sign-' + this.#size + '-' + words).toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 80) + '-print.jpg';
        const dt = new DataTransfer();
        dt.items.add(new File([blob], name, { type: 'image/jpeg' }));
        field.files = dt.files;
        const proofField = this.querySelector('[data-btf-signs-proof]');
        if (proofField && pic.proof) {
          const dp = new DataTransfer();
          dp.items.add(new File([pic.proof], name.replace(/-print\.jpg$/, '-proof.jpg'), { type: 'image/jpeg' }));
          proofField.files = dp.files;
        }
      } catch (err) {
        field.value = '';
      }
    }, 600);
  }

  #share(open) {
    const share = this.querySelector('btf-share');
    if (!share) return;
    const words = () =>
      ROLES.map((r) => (this.#field(r) || {}).value || '')
        .filter(Boolean)
        .join(' · ');
    share.provider = {
      params: () => {
        const p = new URLSearchParams();
        p.set('size', this.#size);
        ROLES.forEach((role) => {
          const input = this.#field(role);
          if (input && input.value) p.set(role, input.value);
        });
        return p;
      },
      tokens: () => ({
        words: words() ? ', “' + words() + '”' : '',
        size: (this.querySelector('[data-btf-size-line]') || {}).textContent || '',
        price: this.dataset['price' + (this.#size === 'large' ? 'Large' : 'Small')] || '',
      }),
      picture: () => this.#picture(),
    };
    const show = () => (share.hidden = false);
    if (open) show();
    else {
      this.addEventListener('input', (e) => e.isTrusted && show());
      this.addEventListener('click', (e) => e.isTrusted && e.target instanceof Element && e.target.closest('[data-btf-size]') && show());
    }
  }

  #field(role) {
    return this.querySelector('[data-btf-signs-field][data-role="' + role + '"]');
  }

  #setSize(size, silent) {
    this.#size = size === 'large' ? 'large' : 'small';
    this.querySelectorAll('[data-btf-size]').forEach((b) => {
      const on = b.dataset.btfSize === this.#size;
      b.classList.toggle('is-on', on);
      b.setAttribute('aria-pressed', on ? 'true' : 'false');
    });
    const id = this.dataset['variant' + (this.#size === 'large' ? 'Large' : 'Small')];
    this.querySelectorAll('[data-btf-signs-variant]').forEach((i) => {
      if (id) i.value = id;
    });
    const price = this.dataset['price' + (this.#size === 'large' ? 'Large' : 'Small')] || '';
    this.querySelectorAll('[data-btf-basket-price]').forEach((el) => {
      el.textContent = price ? ' — ' + price : '';
    });
    this.#render(silent);
  }

  #render() {
    const P = SIGN[this.#size];
    // the two panels have different limits, so switching width re-clamps what is already typed
    const words = {};
    ROLES.forEach((role) => {
      const input = this.#field(role);
      if (!input) {
        words[role] = '';
        return;
      }
      const cap = P.max[role];
      const clean = input.value.toUpperCase().slice(0, cap);
      if (clean !== input.value) input.value = clean;
      input.setAttribute('maxlength', String(cap));
      words[role] = clean;
      this.querySelectorAll('[data-btf-count="' + role + '"]').forEach((el) => {
        el.textContent = clean.length + ' / ' + cap;
      });
    });

    const g = geometry(this.#size, words.l1, words.l2, words.code, words.strap);

    this.querySelectorAll('btf-signs-panel').forEach((p) => {
      p.dataset.size = this.#size;
      p.dataset.l1 = words.l1;
      p.dataset.l2 = words.l2;
      p.dataset.code = words.code;
      p.dataset.strap = words.strap;
    });

    const price = this.dataset['price' + (this.#size === 'large' ? 'Large' : 'Small')] || '';
    this.querySelectorAll('[data-btf-size-line]').forEach((el) => {
      el.textContent = price ? P.mm + ' · ' + price : P.mm;
    });
    this.querySelectorAll('[data-btf-set-at]').forEach((el) => {
      el.textContent =
        'Set at ' +
        Math.round(g.capMm) +
        ' mm capital height on a ' +
        P.mm +
        ' panel' +
        (g.twoLine ? ', over two lines' : '');
    });
    this.querySelectorAll('[data-btf-warn]').forEach((el) => {
      el.hidden = !g.tooSmall;
    });
    this.#queuePrint();
  }
}

if (!customElements.get('btf-signs-panel')) customElements.define('btf-signs-panel', SignsPanel);
if (!customElements.get('btf-signs-maker')) customElements.define('btf-signs-maker', SignsMaker);
